import type { QueryClient } from '@tanstack/react-query';
import { invalidateCrmEntityQueries } from '@/lib/queryClient';

/**
 * Chiavi query canoniche per le entita' CRM.
 *
 * Perche' esiste questo modulo (22 lug 2026): nel client convivevano DUE forme
 * per la stessa risorsa, e non si incontravano mai.
 *
 *   forma dettaglio (quella che le query registrano davvero):
 *     useCompany  -> ['/api/companies/3060']
 *   forma invalidazione scritta a mano:
 *     EnrichAI    -> ['/api/companies', 3060]
 *
 * React Query fa prefix-match sugli ELEMENTI dell'array: la stringa
 * '/api/companies' non e' prefisso dell'elemento '/api/companies/3060', sono
 * due chiavi diverse. Ogni invalidazione scritta nella seconda forma e' un
 * no-op silenzioso: nessun errore, nessun refetch, la scheda resta ferma sui
 * dati vecchi. Caso reale: EnrichAI su Keke's Breakfast Cafe scriveva 21 campi
 * a DB (email, telefono, indirizzo, social) e la scheda continuava a mostrare
 * "Clicca per aggiungere email...".
 *
 * Regola: nessuno costruisce piu' a mano la chiave di un'entita'. Le query
 * usano `entityDetailKey`, chi deve far ricaricare usa `invalidateEntity`, e
 * cosi' le due parti non possono piu' divergere. Il guard strutturale in
 * tests/unit/query-key-invalidation-contract.test.ts impedisce che la forma
 * a due elementi rientri.
 */

/** Risorse con una pagina/scheda di dettaglio che si registra come `/api/<risorsa>/<id>`. */
export type EntityResource =
  | 'companies'
  | 'contacts'
  | 'deals'
  | 'leads'
  | 'branches'
  | 'brands'
  | 'meetings'
  | 'tasks'
  | 'synergies';

const crmQueryEntityByResource: Partial<Record<EntityResource, string>> = {
  companies: 'company',
  contacts: 'contact',
  deals: 'deal',
  leads: 'lead',
  branches: 'branch',
  brands: 'brand',
  meetings: 'meeting',
  tasks: 'task',
  synergies: 'synergy',
};

/** Chiave dell'elenco di una risorsa. */
export function entityListKey(resource: EntityResource): [string] {
  return [`/api/${resource}`];
}

/** Chiave del dettaglio di un record: l'UNICA forma valida. */
export function entityDetailKey(resource: EntityResource, id: number | string): [string] {
  return [`/api/${resource}/${id}`];
}

/** Chiave di una sotto-risorsa del dettaglio (es. `/api/companies/3060/contacts`). */
export function entitySubKey(resource: EntityResource, id: number | string, segment: string): [string] {
  return [`/api/${resource}/${id}/${segment}`];
}

/**
 * Fa ricaricare un record e l'elenco che lo contiene.
 *
 * Da usare ovunque una scrittura lato server cambi un record: e' l'unico punto
 * che conosce la forma delle chiavi, quindi non puo' sbagliarla. `subSegments`
 * copre le sotto-risorse che dipendono dallo stesso record (contatti, note,
 * partecipanti...).
 */
export function invalidateEntity(
  queryClient: QueryClient,
  resource: EntityResource,
  id: number | string | null | undefined,
  subSegments: string[] = [],
): void {
  queryClient.invalidateQueries({ queryKey: entityListKey(resource) });
  const crmQueryEntity = crmQueryEntityByResource[resource];
  if (crmQueryEntity) void invalidateCrmEntityQueries(queryClient, crmQueryEntity);
  if (id === null || id === undefined || id === '') return;
  queryClient.invalidateQueries({ queryKey: entityDetailKey(resource, id) });
  for (const segment of subSegments) {
    queryClient.invalidateQueries({ queryKey: entitySubKey(resource, id, segment) });
  }
}

/**
 * La pagina Deal legge dal read-model `crm-query/execute`, mentre Kanban e
 * picker usano endpoint tradizionali. Dopo ogni mutazione vanno invalidati
 * entrambi: invalidare solo `/api/deals` lasciava Opportunita' ferma.
 */
export function invalidateDealViews(
  queryClient: QueryClient,
  id?: number | string | null,
  subSegments: string[] = [],
): void {
  invalidateEntity(queryClient, 'deals', id, subSegments);
  queryClient.invalidateQueries({ queryKey: ['/api/deals/by-stage'] });
  // Un cambio tardivo o una correzione di Azienda/Contatto/Mandante deve
  // sparire dalla vecchia scheda e comparire nella nuova senza attendere la
  // scadenza della cache. Queste query si registrano con URL completi, quindi
  // ['/api/deals'] da solo non le raggiunge.
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = String(query.queryKey[0] ?? '');
      return /^\/api\/(?:companies|contacts)\/\d+\/deals$/.test(key);
    },
  });
}

export type CrmAssociationTarget = {
  entityType: 'contact' | 'company' | 'branch' | 'lead' | 'deal';
  entityId: number;
};

/**
 * Invalida tutte le proiezioni di una associazione Email--CRM confermata.
 * Il writer server resta la fonte di verita'; questo helper impedisce che la
 * mailbox mostri il nuovo bordo mentre la scheda Deal/Azienda/Persona conserva
 * la fotografia precedente fino a un refresh manuale.
 */
export function invalidateEmailCrmAssociationViews(
  queryClient: QueryClient,
  targets: CrmAssociationTarget[],
): void {
  queryClient.invalidateQueries({ queryKey: ['/api/emails'] });
  queryClient.invalidateQueries({ queryKey: ['/api/emails/counts'] });

  const seen = new Set<string>();
  for (const target of targets) {
    const entityId = Number(target.entityId);
    if (!Number.isSafeInteger(entityId) || entityId <= 0) continue;
    const key = `${target.entityType}:${entityId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    queryClient.invalidateQueries({ queryKey: ['entity-email-panel', target.entityType, entityId] });
    queryClient.invalidateQueries({ queryKey: ['entity-emails', target.entityType, entityId] });
    if (target.entityType === 'deal') invalidateDealViews(queryClient, entityId);
  }
}

/**
 * Il Calendario legge i Meeting dal read-model `crm-query/execute`, mentre
 * schede e picker usano anche gli endpoint tradizionali. Una mutazione deve
 * quindi ricaricare entrambe le superfici: invalidare soltanto
 * `/api/meetings` lasciava il Calendario e le sue card su dati precedenti.
 */
export function invalidateMeetingViews(
  queryClient: QueryClient,
  id?: number | string | null,
  subSegments: string[] = [],
): void {
  invalidateEntity(queryClient, 'meetings', id, subSegments);
  queryClient.invalidateQueries({ queryKey: ['/api/meetings/upcoming'] });
}
